import React, { useState, useEffect, useCallback } from "react";
import AuthService from "services/auth.service";
import useBalanceData from "views/ModernDashboard/hooks/useBalanceData";
import ModernModal from "components/ModernModal";
import styles from "./VirtualCards.module.css";

const F = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(parseFloat(v) || 0);

const formatCardNumber = (num) =>
  num ? num.replace(/(\d{4})/g, "$1 ").trim() : "";

const CARD_TYPES = [
  { id: "visa", label: "Visa" },
  { id: "verve", label: "Verve" },
  { id: "mastercard", label: "Mastercard" },
];

const MAX_CARDS = 3;

function cardNetworkLabel(type) {
  const map = { visa: "VISA", verve: "VERVE", mastercard: "MASTERCARD" };
  return map[type] || "CARD";
}

export default function VirtualCards() {
  const { availableBalance, currentUser } = useBalanceData();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showRequest, setShowRequest] = useState(false);
  const [requestType, setRequestType] = useState("visa");
  const [holderName, setHolderName] = useState("");
  const [alias, setAlias] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestDone, setRequestDone] = useState(false);

  const [showFreezeConfirm, setShowFreezeConfirm] = useState(false);
  const [freezeTarget, setFreezeTarget] = useState(null);
  const [freezing, setFreezing] = useState(false);

  const fetchCards = useCallback(async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }
    try {
      const res = await AuthService.getUserVirtualCard();
      setCards(Array.isArray(res) ? res : []);
    } catch {
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const openRequest = () => {
    setRequestError("");
    setRequestDone(false);
    setRequestType("visa");
    setHolderName(`${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim());
    setAlias("");
    setShowRequest(true);
  };

  const handleRequest = async () => {
    if (!holderName.trim()) {
      setRequestError("Card holder name is required");
      return;
    }
    setRequesting(true);
    setRequestError("");
    try {
      await AuthService.requestVirtualCard(holderName.trim(), requestType, alias.trim() || undefined);
      setRequestDone(true);
      fetchCards();
    } catch (err) {
      setRequestError(err.response?.data?.message || err.message || "Request failed");
    } finally {
      setRequesting(false);
    }
  };

  const openFreezeConfirm = (card) => {
    setFreezeTarget(card);
    setShowFreezeConfirm(true);
  };

  const handleToggleFreeze = async () => {
    if (!freezeTarget?.id) return;
    setFreezing(true);
    try {
      const res = await AuthService.freezeVirtualCard(freezeTarget.id);
      setCards((prev) =>
        prev.map((c) => (c.id === freezeTarget.id ? { ...c, isFrozen: res.card.isFrozen } : c))
      );
    } catch {
      /* ignore */
    } finally {
      setFreezing(false);
      setShowFreezeConfirm(false);
      setFreezeTarget(null);
    }
  };

  const handleCopyDetails = (card) => {
    const text = `Card: ${card.cardNumber}\nExpiry: ${card.expiryDate}\nCVV: ${card.cvv}\nHolder: ${card.cardHolderName}`;
    navigator.clipboard.writeText(text).catch(() => {});
    alert("Card details copied to clipboard");
  };

  const pendingCards = cards.filter((c) => c.status === "pending");
  const rejectedCards = cards.filter((c) => c.status === "rejected");
  const canRequest = cards.filter((c) => c.status === "pending" || c.status === "approved").length < MAX_CARDS;

  return (
    <div className="content mid-content">
      {/* ---- Page Header ---- */}
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Virtual Cards</h2>
          <p className={styles.pageSubtitle}>
            Manage your virtual debit cards linked to your balance.
          </p>
        </div>
        <button
          className={styles.primaryBtn}
          onClick={openRequest}
          disabled={!canRequest}
          title={canRequest ? "Request a new card" : `You can have up to ${MAX_CARDS} cards`}
        >
          ＋ Request New Card
        </button>
      </div>

      {!canRequest && (
        <p className={styles.limitNote}>
          You've reached the maximum of {MAX_CARDS} cards.
        </p>
      )}

      {loading ? (
        <div className={styles.heroSkeleton}>
          <div className={styles.skel} style={{ width: "50%", height: 16 }} />
          <div className={styles.skel} style={{ width: "80%", height: 24 }} />
          <div className={styles.skel} style={{ width: 160, height: 44, borderRadius: "var(--radius-sm)" }} />
        </div>
      ) : cards.length === 0 ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptyIcon}>🪪</div>
          <h3 className={styles.emptyTitle}>No Virtual Cards Yet</h3>
          <p className={styles.emptyText}>
            Link a virtual card to your <strong>{F(availableBalance)}</strong> balance.
          </p>
          <ul className={styles.benefits}>
            <li>✓ Visa, Verve, or Mastercard</li>
            <li>✓ Freeze instantly if you need to</li>
            <li>✓ No physical card required</li>
          </ul>
          <button className={styles.primaryBtn} onClick={openRequest}>
            🚀 Request Your First Card
          </button>
        </div>
      ) : (
        <div className={styles.cardList}>
          {cards.map((card) => {
            const isApproved = card.status === "approved";
            const isFrozen = card.isFrozen;
            return (
              <VirtualCardItem
                key={card.id}
                card={card}
                isApproved={isApproved}
                isFrozen={isFrozen}
                onFreeze={() => openFreezeConfirm(card)}
                onCopy={() => handleCopyDetails(card)}
              />
            );
          })}

          {/* Pending summary */}
          {pendingCards.length > 0 && (
            <div className={styles.pendingSummary}>
              <h3 className={styles.sectionTitle}>Pending Requests</h3>
              {pendingCards.map((c) => (
                <div key={c.id} className={styles.pendingRow}>
                  <span>{cardNetworkLabel(c.cardType)} · {c.cardHolderName}</span>
                  <span className={styles.pendingChip}>PENDING</span>
                </div>
              ))}
            </div>
          )}

          {/* Rejected summary */}
          {rejectedCards.length > 0 && (
            <div className={styles.pendingSummary}>
              <h3 className={styles.sectionTitle}>Rejected Requests</h3>
              {rejectedCards.map((c) => (
                <div key={c.id} className={styles.pendingRow}>
                  <span>{cardNetworkLabel(c.cardType)} · {c.cardHolderName}</span>
                  <span className={styles.rejectedChip}>REJECTED</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Request New Card Modal ---- */}
      <ModernModal
        isOpen={showRequest}
        onClose={() => setShowRequest(false)}
        title="Request New Card"
        size="sm"
      >
        {requestDone ? (
          <div className={styles.modalGrid}>
            <div className={styles.modalIcon}>⏳</div>
            <p className={styles.modalText}>
              Card request submitted! Once approved, your card details will appear here.
            </p>
            <button className={styles.primaryBtn} onClick={() => setShowRequest(false)}>
              Done
            </button>
          </div>
        ) : (
          <div className={styles.modalGrid}>
            <div className={styles.typeSelector}>
              {CARD_TYPES.map((t) => (
                <button
                  key={t.id}
                  className={`${styles.typeOption} ${requestType === t.id ? styles.typeOptionActive : ""}`}
                  onClick={() => setRequestType(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Card Holder Name *</label>
              <input
                type="text"
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
                placeholder="Enter card holder name"
              />
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Alias (optional)</label>
              <input
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="e.g. Travel, Shopping"
              />
            </div>

            {requestError && (
              <p className={styles.errorText}>{requestError}</p>
            )}

            <button
              className={styles.primaryBtn}
              onClick={handleRequest}
              disabled={requesting}
            >
              {requesting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        )}
      </ModernModal>

      {/* ---- Freeze confirmation modal ---- */}
      <ModernModal
        isOpen={showFreezeConfirm}
        onClose={() => setShowFreezeConfirm(false)}
        title={freezeTarget?.isFrozen ? "Unfreeze Card" : "Freeze Card"}
        size="sm"
      >
        <div className={styles.modalGrid}>
          <div className={styles.modalIcon}>{freezeTarget?.isFrozen ? "🔓" : "❄️"}</div>
          <p className={styles.modalText}>
            {freezeTarget?.isFrozen
              ? "Unfreeze this card to allow new charges again?"
              : "Freeze this card to instantly block all new charges?"}
          </p>
          <div className={styles.modalActions}>
            <button className={styles.secondaryBtn} onClick={() => setShowFreezeConfirm(false)}>
              Cancel
            </button>
            <button
              className={styles.primaryBtn}
              onClick={handleToggleFreeze}
              disabled={freezing}
            >
              {freezing ? "..." : freezeTarget?.isFrozen ? "Unfreeze" : "Freeze"}
            </button>
          </div>
        </div>
      </ModernModal>
    </div>
  );
}

function VirtualCardItem({ card, isApproved, isFrozen, onFreeze, onCopy }) {
  const [showCvv, setShowCvv] = useState(false);

  if (!isApproved) return null;

  return (
    <div className={`${styles.heroCard} ${isFrozen ? styles.heroFrozen : styles.heroActive}`}>
      <div className={styles.heroWave} />
      <div className={styles.heroTop}>
        <div className={styles.heroBrandWrap}>
          <span className={styles.heroBrand}>{cardNetworkLabel(card.cardType)}</span>
          {card.alias && <span className={styles.aliasLabel}>· {card.alias}</span>}
        </div>
        {card.isPrimary && <span className={styles.primaryBadge}>PRIMARY</span>}
      </div>

      <div className={styles.heroChip}>💳</div>
      <div className={styles.heroNumber}>
        {isFrozen ? `•••• •••• •••• ${card.lastFour}` : formatCardNumber(card.cardNumber)}
      </div>
      <div className={styles.heroBottom}>
        <div>
          <span className={styles.heroLabel}>Card Holder</span>
          <span className={styles.heroValue}>{card.cardHolderName}</span>
        </div>
        <div>
          <span className={styles.heroLabel}>Expires</span>
          <span className={styles.heroValue}>{card.expiryDate || "—"}</span>
        </div>
        <div>
          <span className={styles.heroLabel}>CVV</span>
          <button
            type="button"
            className={styles.cvvToggle}
            onClick={() => setShowCvv((v) => !v)}
          >
            {isFrozen ? "•••" : showCvv ? card.cvv : "••• 👁"}
          </button>
        </div>
      </div>

      <div className={styles.cardActions}>
        <button className={styles.secondaryBtn} onClick={onFreeze}>
          {isFrozen ? "🔓 Unfreeze" : "🔒 Freeze"}
        </button>
        <button className={styles.secondaryBtn} onClick={onCopy}>
          📋 Copy
        </button>
      </div>

      {isFrozen && (
        <div className={styles.frozenBadge}>
          <span>❄️</span> CARD FROZEN
        </div>
      )}
    </div>
  );
}